import { HttpErrorResponse } from '@angular/common/http';
import { computed, Injector, Signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  BalanceDto,
  NewOrderRequest,
  RateRequest,
  RateResponse,
  TradingControllerService,
} from '@api/client';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStoreFeature,
  type,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { Maybe } from '@stillmandigital/common';
import { FatFingerPayload } from 'app/dashboard/components/fat-finger-dialog/fat-finger-dialog.component';
import { mergeMap, pipe } from 'rxjs';
import { PairViewModel } from '../dashboard.interfaces';

export const useTrading = () =>
  signalStoreFeature(
    {
      props: {
        _activeAccountId: type<Signal<number | undefined>>(),
        _injector: type<Injector>(),
        _dialogService: type<MatDialog>(),
        _notificationService: type<NotificationService>(),
        _tradingService: type<TradingControllerService>(),
        // Provided by useBalances / usePairs — must be composed before this feature.
        allBalances: type<Signal<BalanceDto[]>>(),
        baseAssetName: type<Signal<string>>(),
        quoteAssetName: type<Signal<string>>(),
      },
      state: {
        selectedPair: type<Maybe<PairViewModel>>(),
      },
    },
    withState({
      rate: undefined as RateResponse | undefined,
      rateFilter: undefined as RateRequest | undefined,
      tradeLoading: false,
      limitPrice: undefined as number | undefined,
    }),
    withComputed(store => {
      const freeAmountOf = (asset: string) =>
        store.allBalances().find(balance => balance.asset === asset)?.free || 0;

      const baseAssetFreeAmount = computed(() => freeAmountOf(store.baseAssetName()));

      const quoteAssetFreeAmount = computed(() => freeAmountOf(store.quoteAssetName()));

      return { baseAssetFreeAmount, quoteAssetFreeAmount };
    }),
    withMethods(store => {
      const setRate = (rate: RateResponse) => patchState(store, { rate });

      const updateRateFilter = (rateFilter: RateRequest | undefined) =>
        patchState(store, { rateFilter });

      const setLimitPrice = (limitPrice: number) =>
        patchState(store, {
          limitPrice: Number(limitPrice.toFixed(store.selectedPair()?.priceScale)),
        });

      const trade = rxMethod<NewOrderRequest>(
        pipe(
          mergeMap(request => {
            patchState(store, { tradeLoading: true });

            return store._tradingService
              .requestTrade({ ...request, accountId: store._activeAccountId() })
              .pipe(
                tapResponse({
                  next: () => patchState(store, { tradeLoading: false }),
                  error: (error: HttpErrorResponse) => {
                    patchState(store, { tradeLoading: false });
                    store._notificationService.showErrorNotification(
                      'NOTIFICATION.THE_TRADE_COULDNT_BE_INITIATED',
                      error,
                    );
                  },
                }),
              );
          }),
        ),
      );

      const openFatFingerDialog = async (payload: FatFingerPayload) => {
        const { FatFingerDialogComponent } =
          await import('app/dashboard/components/fat-finger-dialog/fat-finger-dialog.component');

        store._dialogService.open(FatFingerDialogComponent, {
          data: payload,
          injector: store._injector,
        });
      };

      const openOrderConfirmationDialog = async (request: NewOrderRequest) => {
        const { OrderConfirmationDialogComponent } =
          await import('app/dashboard/components/order-confirmation-dialog/order-confirmation-dialog.component');

        store._dialogService.open(OrderConfirmationDialogComponent, {
          data: request,
          injector: store._injector,
        });
      };

      return {
        setRate,
        updateRateFilter,
        setLimitPrice,
        trade,
        openFatFingerDialog,
        openOrderConfirmationDialog,
      };
    }),
  );
